import { EyeCrossLineWhite, EyeGray400, radio_selected, radio_unselected } from '@assets/icons';
import {
  MenuTop,
  Output,
  Text,
  CharacterNumberInput,
  Chip,
  Select,
  CheckboxItem,
  TagSearchInput,
  SearchResetButton
} from '@components';
import {
  AgeRangeChildOptionValueEnum,
  CharacterNumberInputType,
  ChipPaperType,
  HorizontalAngleChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  OutputSortTypeEnum,
  PostureCategoryChildOptionValueEnum,
  SelectedComicOptionEnum,
  StandardFacialExpressionChildOptionValueEnum,
  StandardFrameChildOptionValueEnum,
  StandardGenreValueEnum,
  styleConstant,
  VerticalAngleChildOptionValueEnum
} from '@constant';
import { useComic, useOutput, useStandard } from '@hooks';
import { MenuItem, Radio } from '@mui/material';
import { useComicActions, useGeneralActions, useStandardActions } from '@remote-hooks';
import { colorSet } from '@theme';
import { throttle } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

const StandardOutputPage = () => {
  const {
    selectedOption,
    handleOptionChange,
    selectedGenreList,
    handleGenreToggle,
    isGenreSelected,
    selectedComicNameList,
    inputKeyword,
    autocompleteList,
    handleKeyDown,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicTagClear
  } = useComic();

  const { characterNumber, setCharacterNumber, getMaleNumberMenuItems, getFemaleNumberMenuItems } = useStandard();

  const { storedStandardChildOptionValue, onSetStandard, handleIncludeUsedToggle, handleIncludeOtherUsersUsedToggle } =
    useOutput();

  // 스크롤다운에 따른 layout 분기용 state
  const [isScrollDown, setIsScrollDown] = useState<boolean>(false);
  // 마운트 상태를 추적하는 state
  const [isComponentMounted, setIsComponentMounted] = useState(false);

  // StandardGenreValueEnum의 값들을 배열로 변환
  const genreOptions = Object.values(StandardGenreValueEnum);

  // 정렬 기준 Select 컴포넌트의 value를 영어가 아닌 한국어로 보여주기 위한 변수
  const SORT_LABELS: Record<OutputSortTypeEnum, string> = {
    [OutputSortTypeEnum.RANDOM]: '랜덤순',
    [OutputSortTypeEnum.LOW_USAGE]: '사용량 낮음'
  };

  // 인원수 의존성 필터 disabled 로직을 위한 변수
  const isCharacterNumberOne = storedStandardChildOptionValue.characterNumber
    ? storedStandardChildOptionValue.characterNumber.isWholeGenderNumberChecked
      ? storedStandardChildOptionValue.characterNumber.wholeGenderNumber === 1
      : typeof storedStandardChildOptionValue.characterNumber.maleNumber === 'number' &&
        typeof storedStandardChildOptionValue.characterNumber.femaleNumber === 'number' &&
        storedStandardChildOptionValue.characterNumber.maleNumber +
          storedStandardChildOptionValue.characterNumber.femaleNumber ===
          1
    : false;

  const { onInitComic } = useComicActions();
  const { onInitGeneral } = useGeneralActions();
  const { onInitStandard } = useStandardActions();

  // Throttled scroll handler
  const handleScroll = useCallback(
    throttle(() => {
      if (window.scrollY > styleConstant.topPageNameHeight) {
        setIsScrollDown(true);
      } else {
        setIsScrollDown(false);
      }
    }, 200), // 200ms 간격으로 호출되도록 설정
    []
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // init store
  useEffect(() => {
    setIsComponentMounted(true);
    if (!isComponentMounted) return;

    return () => {
      onInitComic();
      onInitGeneral();
      onInitStandard();
    };
  }, [isComponentMounted, onInitComic, onInitGeneral, onInitStandard]);

  return (
    <div id="standard-search-output-id" style={{ display: 'flex', flexDirection: 'column' }}>
      <MenuTop pageName={isScrollDown ? '스탠다드 검색' : undefined} />

      {/* 상단: 페이지 타이틀 & 장르 및 작품명 */}
      <div
        className="topFilter"
        style={{
          backgroundColor: colorSet.white,
          height: 24 + 132, // paddingTop + frame height
          padding: '0px 40px',
          display: 'flex',
          alignItems: 'flex-end'
        }}
      >
        <div style={{ width: '100%' }}>
          {isScrollDown ? null : (
            <Text variant="h2" fontWeight="bold" marginBottom="24px">
              스탠다드 검색
            </Text>
          )}

          <div style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
            {/* 검색 옵션: 장르 */}
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                <Radio
                  checked={selectedOption === SelectedComicOptionEnum.GENRE}
                  onChange={() => handleOptionChange(SelectedComicOptionEnum.GENRE)}
                  checkedIcon={<img src={radio_selected} alt="" style={{ width: 20, height: 20 }} />}
                  icon={<img src={radio_unselected} alt="" style={{ width: 20, height: 20 }} />}
                />
                <Text variant="subtitle" fontWeight="bold">
                  장르
                </Text>
              </div>

              <Select
                disabled={selectedOption !== SelectedComicOptionEnum.GENRE}
                multiple={true}
                value={selectedGenreList}
                renderValue={(selected: any) => {
                  if (selected && selected.length > 0) {
                    return selected.join(', ');
                  } else {
                    return (
                      <Text variant="body1" color={colorSet.gray400}>
                        {selectedOption !== SelectedComicOptionEnum.GENRE
                          ? '작품명 입력 시 장르는 자동 선택됩니다.'
                          : '장르를 1개 이상 선택해주세요.'}
                      </Text>
                    );
                  }
                }}
                boxstyle={{ width: 344 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 386,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '0px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: '8px',
                        backgroundColor: colorSet.primaryMain
                      },
                      '&::-webkit-scrollbar-track': {
                        borderRadius: '8px',
                        backgroundColor: colorSet.gray200
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: colorSet.white
                      }
                    }
                  }
                }}
              >
                <MenuItem value={''} disabled>
                  장르
                </MenuItem>
                {genreOptions.map((genre) => (
                  <MenuItem key={genre} value={genre} onClick={() => handleGenreToggle(genre)}>
                    <CheckboxItem
                      label={genre}
                      isChecked={isGenreSelected(genre)}
                      onChange={() => handleGenreToggle(genre)}
                    />
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* 검색 옵션: 작품명 */}
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                <Radio
                  checked={selectedOption === SelectedComicOptionEnum.COMIC_NAME}
                  onChange={() => handleOptionChange(SelectedComicOptionEnum.COMIC_NAME)}
                  checkedIcon={<img src={radio_selected} alt="" style={{ width: 20, height: 20 }} />}
                  icon={<img src={radio_unselected} alt="" style={{ width: 20, height: 20 }} />}
                />
                <Text variant="subtitle" fontWeight="bold">
                  작품명
                </Text>
              </div>

              <TagSearchInput
                placeholder={
                  selectedOption !== SelectedComicOptionEnum.COMIC_NAME
                    ? '장르 선택 시 해당 장르의 작품이 전체 검색됩니다.'
                    : '작품명 입력 후 엔터를 누르세요.'
                }
                tags={selectedComicNameList}
                inputValue={inputKeyword}
                options={autocompleteList}
                onKeyDown={handleKeyDown}
                onInputChange={handleInputKeywordChange}
                onAutocompleteChange={handleAutocompleteChange}
                onTagClear={handleComicTagClear}
                disabled={selectedOption !== SelectedComicOptionEnum.COMIC_NAME}
                boxstyle={{ width: 344 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 상단: 필터 초기화 & Chip & 태그 검색 인풋 */}
      <div
        style={{
          width: '100%',
          position: 'sticky',
          top: styleConstant.menuTopHeight,
          backgroundColor: colorSet.white,
          borderBottom: `1px solid ${colorSet.gray300}`,
          padding: '24px 40px',
          zIndex: styleConstant.zIndexTopFilter
        }}
      >
        <SearchResetButton boxstyle={{ marginBottom: '12px' }} />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: `12px`,
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <Chip
            label="배경컷"
            needTooltip={true}
            tooltipText={`배경컷이 포함된 경우, 다음 항목은 선택할 수 없습니다.
                
            인원수 / 연령대 / 스킨십 / 중심 동작 부위 / 표정 / 가로앵글 / 데포르메 / 동작구분`}
            options={{
              포함: { name: '포함', value: 'true', isSelected: false },
              제외: { name: '제외', value: 'false', isSelected: false }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.backgroundCut}
            setSelectedOption={(param) => {
              const isTrueSelected = param?.포함.isSelected; // '포함'이 선택된 경우 의존성 필터값 null 부여

              if (isTrueSelected) {
                setCharacterNumber(null);

                onSetStandard({
                  backgroundCut: param,
                  characterNumber: null,
                  ageRange: null,
                  mainMotionPart: null,
                  facialExpression: null,
                  deforme: null,
                  horizontalAngle: null,
                  postureCategory: null,
                  skinship: null
                });
              } else {
                onSetStandard({
                  backgroundCut: param
                });
              }
            }}
          />
          <CharacterNumberInput
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            needTooltip={true}
            tooltipText="인원수가 1명일 경우, 스킨십 항목은 선택할 수 없습니다."
            type={CharacterNumberInputType.CHIP}
            characterNumber={characterNumber}
            onMaleNumberChange={(value: number | string) => {
              setCharacterNumber({
                ...characterNumber,
                maleNumber: value,
                femaleNumber: characterNumber?.femaleNumber ?? 0
              });
            }}
            onFemaleNumberChange={(value: number | string) => {
              setCharacterNumber({
                ...characterNumber,
                femaleNumber: value,
                maleNumber: characterNumber?.maleNumber ?? 0
              });
            }}
            onWholeGenderNumberChange={(value: number | string) => {
              setCharacterNumber({ ...characterNumber, wholeGenderNumber: value });
            }}
            onIsWholeGenderCheckedChange={(value: boolean) => {
              setCharacterNumber({ ...characterNumber, isWholeGenderNumberChecked: value });
            }}
            getMaleNumberMenuItems={getMaleNumberMenuItems}
            getFemaleNumberMenuItems={getFemaleNumberMenuItems}
            onApplyClick={(values: {
              maleNumber?: number | string;
              femaleNumber?: number | string;
              wholeGenderNumber?: number | string;
              isWholeGenderNumberChecked?: boolean;
            }) => {
              const isCharacterNumberOne = values
                ? values.isWholeGenderNumberChecked
                  ? values.wholeGenderNumber === 1
                  : typeof values.maleNumber === 'number' &&
                    typeof values.femaleNumber === 'number' &&
                    values.maleNumber + values.femaleNumber === 1
                : false; // 인원수 합이 1인 경우 의존성 필터값 null 부여

              if (isCharacterNumberOne) {
                onSetStandard({ characterNumber: values, skinship: null });
              } else {
                onSetStandard({ characterNumber: values });
              }
            }}
            onChipXButtonClick={() => {
              setCharacterNumber({
                maleNumber: 0,
                femaleNumber: 0,
                wholeGenderNumber: 0,
                isWholeGenderNumberChecked: false
              }); //X버튼 누른 이후 다시 인원수 칩 페이퍼를 열었을 시 초기값 보여주기
              onSetStandard({ characterNumber: null });
            }}
          />
          <Chip
            label="프레임"
            paperType={ChipPaperType.CHECKBOX_LIST}
            options={{
              클로즈업: {
                name: '클로즈업',
                value: StandardFrameChildOptionValueEnum['클로즈업'],
                isSelected: false
              },
              미디엄샷: {
                name: '미디엄샷',
                value: StandardFrameChildOptionValueEnum['미디엄샷'],
                isSelected: false
              },
              풀샷: {
                name: '풀샷',
                value: StandardFrameChildOptionValueEnum['풀샷'],
                isSelected: false
              },
              롱샷: {
                name: '롱샷',
                value: StandardFrameChildOptionValueEnum['롱샷'],
                isSelected: false
              }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.frame}
            setSelectedOption={(param) => {
              onSetStandard({
                frame: param
              });
            }}
          />
          <Chip
            label="연출"
            needTooltip={true}
            tooltipText="주로 1인 전신 컷, 배경에 실제 배경이 아닌 분위기를 표현하는 화려한 연출이 포함됩니다."
            options={{
              포함: { name: '포함', value: 'true', isSelected: false },
              제외: { name: '제외', value: 'false', isSelected: false }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.directed}
            setSelectedOption={(param) => {
              onSetStandard({
                directed: param
              });
            }}
          />
          <Chip
            label="세로앵글"
            paperType={ChipPaperType.CHECKBOX_LIST}
            options={{
              로우앵글: {
                name: '로우앵글',
                value: VerticalAngleChildOptionValueEnum['로우앵글'],
                isSelected: false
              },
              하이앵글: {
                name: '하이앵글',
                value: VerticalAngleChildOptionValueEnum['하이앵글'],
                isSelected: false
              },
              항공샷: {
                name: '항공샷',
                value: VerticalAngleChildOptionValueEnum['항공샷'],
                isSelected: false
              },
              평시각: {
                name: '평시각',
                value: VerticalAngleChildOptionValueEnum['평시각'],
                isSelected: false
              }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.verticalAngle}
            setSelectedOption={(param) => {
              onSetStandard({
                verticalAngle: param
              });
            }}
          />
          <Chip
            label="연령대"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            paperType={ChipPaperType.CHECKBOX_LIST}
            options={{
              유아: {
                name: '유아',
                value: AgeRangeChildOptionValueEnum['유아'],
                isSelected: false
              },
              청소년: {
                name: '청소년',
                value: AgeRangeChildOptionValueEnum['청소년'],
                isSelected: false
              },
              청년: {
                name: '청년',
                value: AgeRangeChildOptionValueEnum['청년'],
                isSelected: false
              },
              중장년: {
                name: '중장년',
                value: AgeRangeChildOptionValueEnum['중장년'],
                isSelected: false
              },
              노인: {
                name: '노인',
                value: AgeRangeChildOptionValueEnum['노인'],
                isSelected: false
              }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.ageRange}
            setSelectedOption={(param) => {
              onSetStandard({
                ageRange: param
              });
            }}
          />
          <Chip
            label="중심 동작 부위"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            paperType={ChipPaperType.CHECKBOX_LIST}
            options={{
              손: {
                name: '손',
                value: MainMotionPartChildOptionValueEnum['손'],
                isSelected: false
              },
              팔: {
                name: '팔',
                value: MainMotionPartChildOptionValueEnum['팔'],
                isSelected: false
              },
              다리: {
                name: '다리',
                value: MainMotionPartChildOptionValueEnum['다리'],
                isSelected: false
              },
              발: {
                name: '발',
                value: MainMotionPartChildOptionValueEnum['발'],
                isSelected: false
              },
              머리: {
                name: '머리',
                value: MainMotionPartChildOptionValueEnum['머리'],
                isSelected: false
              },
              전신: {
                name: '전신',
                value: MainMotionPartChildOptionValueEnum['전신'],
                isSelected: false
              },
              상체: {
                name: '상체',
                value: MainMotionPartChildOptionValueEnum['상체'],
                isSelected: false
              },
              하체: {
                name: '하체',
                value: MainMotionPartChildOptionValueEnum['하체'],
                isSelected: false
              }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.mainMotionPart}
            setSelectedOption={(param) => {
              onSetStandard({
                mainMotionPart: param
              });
            }}
          />
          <Chip
            label="표정"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            needTooltip={true}
            tooltipText="표정 필터를 활성화하면, 정면 또는 측면 얼굴이 명확히 드러난 이미지만 출력됩니다."
            paperType={ChipPaperType.FACE_CHECKBOX_LIST}
            options={{
              만족: {
                name: '만족',
                value: StandardFacialExpressionChildOptionValueEnum['만족'],
                isSelected: false
              },
              자애: {
                name: '자애',
                value: StandardFacialExpressionChildOptionValueEnum['자애'],
                isSelected: false
              },
              새침: {
                name: '새침',
                value: StandardFacialExpressionChildOptionValueEnum['새침'],
                isSelected: false
              },
              기쁨: {
                name: '기쁨',
                value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                isSelected: false
              },
              무표정: {
                name: '무표정',
                value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                isSelected: false
              },
              놀람: {
                name: '놀람',
                value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                isSelected: false
              },
              고민: {
                name: '고민',
                value: StandardFacialExpressionChildOptionValueEnum['고민'],
                isSelected: false
              },
              혐오: {
                name: '혐오',
                value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                isSelected: false
              },
              분노: {
                name: '분노',
                value: StandardFacialExpressionChildOptionValueEnum['분노'],
                isSelected: false
              },
              당황: {
                name: '당황',
                value: StandardFacialExpressionChildOptionValueEnum['당황'],
                isSelected: false
              },
              공포: {
                name: '공포',
                value: StandardFacialExpressionChildOptionValueEnum['공포'],
                isSelected: false
              },
              슬픔: {
                name: '슬픔',
                value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                isSelected: false
              },
              섬뜩: {
                name: '섬뜩',
                value: StandardFacialExpressionChildOptionValueEnum['섬뜩'],
                isSelected: false
              },
              경계: {
                name: '경계',
                value: StandardFacialExpressionChildOptionValueEnum['경계'],
                isSelected: false
              },
              좌절: {
                name: '좌절',
                value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                isSelected: false
              },
              뻔뻔: {
                name: '뻔뻔',
                value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                isSelected: false
              },
              수치: {
                name: '수치',
                value: StandardFacialExpressionChildOptionValueEnum['수치'],
                isSelected: false
              },
              의심: {
                name: '의심',
                value: StandardFacialExpressionChildOptionValueEnum['의심'],
                isSelected: false
              },
              불안: {
                name: '불안',
                value: StandardFacialExpressionChildOptionValueEnum['불안'],
                isSelected: false
              },
              절규: {
                name: '절규',
                value: StandardFacialExpressionChildOptionValueEnum['절규'],
                isSelected: false
              },
              경멸: {
                name: '경멸',
                value: StandardFacialExpressionChildOptionValueEnum['경멸'],
                isSelected: false
              }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.facialExpression}
            setSelectedOption={(param) => {
              onSetStandard({
                facialExpression: param
              });
            }}
          />
          <Chip
            label="데포르메"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            options={{
              포함: { name: '포함', value: 'true', isSelected: false },
              제외: { name: '제외', value: 'false', isSelected: false }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.deforme}
            setSelectedOption={(param) => {
              onSetStandard({
                deforme: param
              });
            }}
          />
          <Chip
            label="가로앵글"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            paperType={ChipPaperType.CHECKBOX_LIST}
            options={{
              정면: { name: '정면', value: HorizontalAngleChildOptionValueEnum['정면'], isSelected: false },
              정측면: {
                name: '정측면',
                value: HorizontalAngleChildOptionValueEnum['정측면'],
                isSelected: false
              },
              측면: { name: '측면', value: HorizontalAngleChildOptionValueEnum['측면'], isSelected: false },
              후측면: {
                name: '후측면',
                value: HorizontalAngleChildOptionValueEnum['후측면'],
                isSelected: false
              },
              후면: { name: '후면', value: HorizontalAngleChildOptionValueEnum['후면'], isSelected: false }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.horizontalAngle}
            setSelectedOption={(param) => {
              onSetStandard({ horizontalAngle: param });
            }}
          />
          <Chip
            label="동작구분"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected}
            paperType={ChipPaperType.CHECKBOX_LIST}
            options={{
              서있음: { name: '서있음', value: PostureCategoryChildOptionValueEnum['서있음'], isSelected: false },
              앉음: {
                name: '앉음',
                value: PostureCategoryChildOptionValueEnum['앉음'],
                isSelected: false
              },
              누움: { name: '누움', value: PostureCategoryChildOptionValueEnum['누움'], isSelected: false },
              엎드림: {
                name: '엎드림',
                value: PostureCategoryChildOptionValueEnum['엎드림'],
                isSelected: false
              }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.postureCategory}
            setSelectedOption={(param) => {
              onSetStandard({ postureCategory: param });
            }}
          />
          <Chip
            label="스킨십(12~17+)"
            disabled={storedStandardChildOptionValue.backgroundCut?.포함.isSelected || isCharacterNumberOne}
            needTooltip={true}
            tooltipText={`19+를 제외한 포옹, 키스 등의 접촉 장면을 포함합니다.
              인원수가 2명 이상일 때만 활성화됩니다.`}
            options={{
              포함: { name: '포함', value: 'true', isSelected: false },
              제외: { name: '제외', value: 'false', isSelected: false }
            }}
            storedChildOptionValue={storedStandardChildOptionValue.skinship}
            setSelectedOption={(param) => {
              onSetStandard({
                skinship: param
              });
            }}
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '20px',
          padding: '48px 40px 0px 40px'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 12
          }}
        >
          <div
            onClick={handleIncludeUsedToggle}
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
              padding: '8px 24px',
              alignItems: 'center',
              borderRadius: '22px',
              border: storedStandardChildOptionValue.isIncludeUsed ? `1px solid ${colorSet.gray400}` : 'none',
              cursor: 'pointer',
              backgroundColor: storedStandardChildOptionValue.isIncludeUsed ? 'transparent' : colorSet.gray600
            }}
          >
            {storedStandardChildOptionValue.isIncludeUsed ? <EyeGray400 /> : <EyeCrossLineWhite />}
            <Text
              variant="body2"
              color={storedStandardChildOptionValue.isIncludeUsed ? colorSet.gray400 : colorSet.white}
              fontWeight={storedStandardChildOptionValue.isIncludeUsed ? 400 : 700}
            >
              내가 사용한 이미지
            </Text>
          </div>
          <div
            onClick={handleIncludeOtherUsersUsedToggle}
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
              padding: '8px 24px',
              alignItems: 'center',
              borderRadius: '22px',
              border: storedStandardChildOptionValue.isIncludeOtherUsersUsed ? `1px solid ${colorSet.gray400}` : 'none',
              cursor: 'pointer',
              backgroundColor: storedStandardChildOptionValue.isIncludeOtherUsersUsed ? 'transparent' : colorSet.gray600
            }}
          >
            {storedStandardChildOptionValue.isIncludeOtherUsersUsed ? <EyeGray400 /> : <EyeCrossLineWhite />}
            <Text
              variant="body2"
              color={storedStandardChildOptionValue.isIncludeOtherUsersUsed ? colorSet.gray400 : colorSet.white}
              fontWeight={storedStandardChildOptionValue.isIncludeOtherUsersUsed ? 400 : 700}
            >
              남이 사용한 이미지
            </Text>
          </div>
        </div>
        <Select
          value={storedStandardChildOptionValue.sortType}
          renderValue={(value) => {
            const sortType = value as OutputSortTypeEnum;
            return SORT_LABELS[sortType] || '정렬 기준';
          }}
          onChange={(e) => {
            onSetStandard({ sortType: e.target.value });
          }}
          boxstyle={{ width: 160 }}
        >
          <MenuItem value={''} disabled>
            정렬 기준
          </MenuItem>
          <MenuItem value={OutputSortTypeEnum.RANDOM}>랜덤순</MenuItem>
          <MenuItem value={OutputSortTypeEnum.LOW_USAGE}>사용량 낮음</MenuItem>
        </Select>
      </div>

      {/* 검색 결과 */}
      <div
        style={{
          width: '100%'
        }}
      >
        <Output />
      </div>
    </div>
  );
};

export default StandardOutputPage;
